import { VARIABLE_CATEGORIES } from "./constants";
import { getTypeIcon, getTypeColor } from "./utils";
import {
  DragIndicatorRounded as DragIcon,
  AddRounded as AddIcon,
  CheckCircleRounded as PlacedIcon,
  ExpandMoreRounded as ExpandIcon,
  ChevronRightRounded as CollapseIcon,
  TextFieldsRounded as TextIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Collapse,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Button from "components/common/Button";
import { useState } from "react";

const DraggableVariable = ({ variable, isPlaced }) => {
  const theme = useTheme();
  const typeColor = getTypeColor(variable.type);

  const handleDragStart = (e) => {
    e.dataTransfer.setData("application/json", JSON.stringify(variable));
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <Box
      draggable
      onDragStart={handleDragStart}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        border: `1px solid ${
          isPlaced ? theme.palette.success.main : typeColor
        }`,
        backgroundColor: isPlaced
          ? `${theme.palette.success.main}10`
          : `${typeColor}10`,
        cursor: "grab",
        userSelect: "none",
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: isPlaced
            ? `${theme.palette.success.main}20`
            : `${typeColor}20`,
          transform: "translateY(-1px)",
          boxShadow: theme.shadows[2],
        },
        "&:active": {
          cursor: "grabbing",
        },
      }}
    >
      <DragIcon
        sx={{ fontSize: 14, color: isPlaced ? "success.main" : typeColor }}
      />
      {getTypeIcon(variable.type)}
      <Typography
        variant={"caption"}
        sx={{
          fontWeight: 500,
          color: isPlaced ? "success.main" : typeColor,
        }}
      >
        {variable.label}
      </Typography>
      {isPlaced && (
        <PlacedIcon sx={{ fontSize: 14, color: "success.main", ml: 0.5 }} />
      )}
    </Box>
  );
};

const VariablePalette = ({
  searchTerm,
  onSearchChange,
  placedVariables,
  onRequestNewVariable,
  customVariables = [],
}) => {
  const theme = useTheme();
  const [expandedCategories, setExpandedCategories] = useState({
    Custom: true,
    "Insured / Program": true,
    Dates: true,
    Broker: false,
    Underwriter: false,
    "Layer / Policy": false,
    "Layer Limits & Attachment": false,
    "Premium & Pricing": false,
    Tower: false,
    Endorsements: false,
    Template: false,
    "Followed Policy": false,
    "Prior Submission": false,
    Exposure: false,
  });

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const filteredCategories = Object.entries(VARIABLE_CATEGORIES).reduce(
    (acc, [category, data]) => {
      const filteredItems = data.items.filter(
        (item) =>
          !searchTerm ||
          item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.path.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (filteredItems.length > 0) {
        acc[category] = { ...data, items: filteredItems };
      }
      return acc;
    },
    {}
  );

  const filteredCustomVariables = customVariables.filter(
    (item) =>
      !searchTerm ||
      item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.path.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const placedPaths = new Set(placedVariables.map((v) => v.path));

  return (
    <Paper
      elevation={0}
      sx={{
        width: 280,
        height: "100%",
        borderRight: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant={"subtitle2"} sx={{ fontWeight: 600, mb: 1 }}>
          {"Data Fields"}
        </Typography>
        <TextField
          fullWidth
          size={"small"}
          placeholder={"Search fields..."}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position={"start"}>
                <TextIcon fontSize={"small"} color={"action"} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
        {filteredCustomVariables.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Box
              onClick={() => toggleCategory("Custom")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                p: 0.5,
                borderRadius: 1,
                cursor: "pointer",
                backgroundColor: `${theme.palette.warning.main}10`,
                "&:hover": {
                  backgroundColor: `${theme.palette.warning.main}20`,
                },
              }}
            >
              {expandedCategories.Custom ? (
                <ExpandIcon fontSize={"small"} color={"warning"} />
              ) : (
                <CollapseIcon fontSize={"small"} color={"warning"} />
              )}
              <AddIcon fontSize={"small"} sx={{ color: "warning.main" }} />
              <Typography
                variant={"caption"}
                sx={{ fontWeight: 600, color: "warning.dark" }}
              >
                {"Your Custom Fields"}
              </Typography>
              <Chip
                label={filteredCustomVariables.length}
                size={"small"}
                color={"warning"}
                sx={{ height: 18, fontSize: "0.65rem", ml: "auto" }}
              />
            </Box>
            <Collapse in={expandedCategories.Custom}>
              <Stack spacing={0.5} sx={{ pl: 2, pt: 0.5 }}>
                {filteredCustomVariables.map((variable) => (
                  <Tooltip
                    key={variable.path}
                    title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography
                          variant={"caption"}
                          display={"block"}
                          sx={{ fontWeight: 600 }}
                        >
                          {variable.description || "No description"}
                        </Typography>
                        {variable.dataSource && (
                          <Typography
                            variant={"caption"}
                            display={"block"}
                            sx={{ mt: 0.5, opacity: 0.8 }}
                          >
                            {"Source: "}
                            {variable.dataSource}
                          </Typography>
                        )}
                      </Box>
                    }
                    placement={"right"}
                    arrow
                  >
                    <Box>
                      <DraggableVariable
                        variable={variable}
                        isPlaced={placedPaths.has(variable.path)}
                      />
                    </Box>
                  </Tooltip>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {Object.entries(filteredCategories).map(([category, data]) => (
          <Box key={category} sx={{ mb: 1 }}>
            <Box
              onClick={() => toggleCategory(category)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                p: 0.5,
                borderRadius: 1,
                cursor: "pointer",
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
            >
              {expandedCategories[category] ? (
                <ExpandIcon fontSize={"small"} />
              ) : (
                <CollapseIcon fontSize={"small"} />
              )}
              <Typography variant={"caption"} sx={{ fontWeight: 600 }}>
                {category}
              </Typography>
              <Chip
                label={data.items.length}
                size={"small"}
                sx={{ height: 18, fontSize: "0.65rem", ml: "auto" }}
              />
            </Box>
            <Collapse in={expandedCategories[category]}>
              <Stack spacing={0.5} sx={{ pl: 2, pt: 0.5 }}>
                {data.items.map((variable) => (
                  <Tooltip
                    key={variable.path}
                    title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography
                          variant={"caption"}
                          display={"block"}
                          sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
                        >
                          {variable.path}
                        </Typography>
                        <Typography
                          variant={"caption"}
                          display={"block"}
                          sx={{ mt: 0.5, opacity: 0.8 }}
                        >
                          {"Type: "}
                          {variable.type}
                        </Typography>
                      </Box>
                    }
                    placement={"right"}
                    arrow
                  >
                    <Box>
                      <DraggableVariable
                        variable={variable}
                        isPlaced={placedPaths.has(variable.path)}
                      />
                    </Box>
                  </Tooltip>
                ))}
              </Stack>
            </Collapse>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button
          fullWidth
          size={"small"}
          startIcon={<AddIcon />}
          onClick={onRequestNewVariable}
          sx={{
            backgroundColor: `${theme.palette.warning.main}15`,
            color: "warning.dark",
            "&:hover": {
              backgroundColor: `${theme.palette.warning.main}25`,
            },
          }}
        >
          {"Create Custom Field"}
        </Button>
      </Box>
    </Paper>
  );
};

export default VariablePalette;
